/**
 * The signed-in person's display preferences, applied to the document.
 *
 * Saved on the account (see `/me/preferences`) so they follow the person
 * between devices. A copy is also kept in this browser, read by the inline
 * script in index.html before the first paint: without it, someone who chose
 * dark would see a white flash on every load while the session request ran.
 * That copy is a cache only; the account is the record.
 */

import {
  DEFAULT_USER_PREFERENCES,
  resolveUserPreferences,
  type FontPreference,
  type TextSizePreference,
  type ThemePreference,
  type UserPreferences,
} from '@cornerstone/education-domain'
import { request } from './api'

export type { FontPreference, ThemePreference, UserPreferences }
export type TextSize = TextSizePreference

export const DEFAULT_PREFERENCES: UserPreferences = DEFAULT_USER_PREFERENCES

export type PreferencesPatch = Partial<Omit<UserPreferences, 'greetings'>> & {
  greetings?: Partial<UserPreferences['greetings']>
}

export const savePreferences = (patch: PreferencesPatch) =>
  request<{ preferences: UserPreferences }>('/me/preferences', { method: 'PATCH', body: patch })

const CACHE_KEY = 'cs.edu.display'

let current: UserPreferences = DEFAULT_PREFERENCES
const listeners = new Set<() => void>()

/** The preferences in force right now, for code outside React (formatters). */
export function currentPreferences(): UserPreferences {
  return current
}

/** Re-render hook for components that print times or depend on the theme. */
export function subscribePreferences(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function resolvePreferences(stored: Partial<UserPreferences> | null | undefined): UserPreferences {
  return resolveUserPreferences(stored)
}

/**
 * Puts the preferences on <html> as data attributes; tokens.css does the rest.
 * Theme "system" is left to the stylesheet's prefers-color-scheme query, so it
 * follows the device live without a listener here.
 */
export function applyPreferences(next: UserPreferences) {
  current = next
  const root = document.documentElement
  if (next.theme === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', next.theme)
  root.setAttribute('data-text-size', next.textSize)
  root.setAttribute('data-font', next.font)
  if (next.font === 'legible') loadLegibleFont()
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ theme: next.theme, textSize: next.textSize, font: next.font }),
    )
  } catch {
    // Private mode or blocked storage: the account copy still applies once loaded.
  }
  listeners.forEach((listener) => listener())
}

/** Atkinson Hyperlegible is fetched only for the people who choose it. */
export function loadLegibleFont() {
  if (document.getElementById('font-legible')) return
  const link = document.createElement('link')
  link.id = 'font-legible'
  link.rel = 'stylesheet'
  link.href = 'https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400&display=swap'
  document.head.appendChild(link)
}

/**
 * This browser's copy of the display choices, so the first render matches
 * what display-boot.js already painted instead of resetting it to defaults
 * until the session arrives.
 */
export function cachedPreferences(): UserPreferences {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) ?? 'null') as Partial<UserPreferences> | null
    return resolvePreferences(cached)
  } catch {
    return resolvePreferences(null)
  }
}

// ── Times ────────────────────────────────────────────────────────────────────

/** A time of day in the person's chosen clock: "2:30 PM" or "14:30". */
export function formatTime(value: Date | string | number): string {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return current.clockFormat === '24'
    ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
    : date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
}

/** A wall-clock "HH:MM" string (a period start, a bus time) in the chosen clock. */
export function formatClock(hhmm: string | null | undefined): string {
  if (!hhmm) return ''
  const [h, m] = hhmm.split(':').map(Number)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm
  const date = new Date()
  date.setHours(h!, m!, 0, 0)
  return formatTime(date)
}

/** Date and time together, for timestamps: "Oct 13, 2:30 PM". */
export function formatDateTime(value: Date | string | number, dateOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }): string {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return `${date.toLocaleDateString([], dateOptions)}, ${formatTime(date)}`
}
